import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type {
  PublicationDetailOut,
  PublicationUpdateIn,
} from '@/api/generated/schemas';
import { inclusiveUpper } from './exclusivityWindow';
import type { PickerValue } from './exclusivityWindow';

/**
 * Per-format exclusivity is a `date` on the wire (`YYYY-MM-DD`) naming the
 * first day the product is no longer exclusive — billing prices a day as
 * exclusive while `day < exclusive_until`. It is the same exclusive upper
 * bound as the window's, so it gets the same shift: the operator picks the
 * last exclusive day, `inclusiveUpper` turns a stored value into it, and the
 * patch sends the day after.
 *
 * The backend contract is narrow, so the rules live here rather than in
 * the modal — the repo has no RTL:
 *   - an explicit `null` is rejected (422): exclusivity cannot be cleared,
 *   - a stored day that is not in the future is rejected (422); picking
 *     today as the last exclusive day sends tomorrow, which passes,
 *   - omitting the field leaves the stored value alone, even an expired one.
 *
 * So an empty or unchanged field is simply left out of the payload.
 */

export type FormatKey = 'ebook' | 'audio';

export interface FormatExclusiveTarget {
  key: FormatKey;
  id: number;
  label: string;
  /** Stored `exclusive_until` — the first non-exclusive day — if any. */
  current: string | null;
}

export type FormatExclusiveValues = Record<FormatKey, Dayjs | null>;

const WIRE_FORMAT = 'YYYY-MM-DD';

/** The formats the publication actually has, in display order. */
export const formatExclusiveTargets = (
  record: PublicationDetailOut,
  label: (key: FormatKey) => string
): FormatExclusiveTarget[] =>
  (['ebook', 'audio'] as const).flatMap((key) => {
    const format = record[key];
    return format
      ? [
          {
            key,
            id: format.id,
            label: label(key),
            current: format.exclusive_until ?? null,
          },
        ]
      : [];
  });

/** Stored value → picker value. */
export const toPickerValue = (
  current: string | null | undefined
): Dayjs | null => (current ? dayjs(current).startOf('day') : null);

/** Days before today cannot be picked; today itself is valid. */
export const isDisabledExclusiveDay = (day: Dayjs, now: Dayjs = dayjs()) =>
  day.isBefore(now, 'day');

/** A legacy value that has already expired — the operator needs a new one. */
export const isPastValue = (value: Dayjs | null, now: Dayjs = dayjs()) =>
  !!value && value.isBefore(now, 'day');

/**
 * The window's end as a date the products can take, or null when it cannot
 * carry over.
 *
 * Both pickers hold the inclusive last day, so the window's last day is the
 * product's — the patch then stores the day after, the window's own upper
 * bound. The two fields still disagree on what is legal: a window is free to
 * be open-ended or to sit entirely in the past, neither of which a product
 * will accept. Both refusals live here so the modal has one question to ask.
 */
export const productDateFromWindow = (
  value: PickerValue,
  now: Dayjs = dayjs()
): Dayjs | null => {
  const upper = value?.[1] ?? null;
  if (!upper || isPastValue(upper, now)) return null;
  return upper.startOf('day');
};

/** Stored `exclusive_until` → the last exclusive day, for the picker. */
export const exclusiveUntilToPickerValue = (
  current: string | null | undefined
): Dayjs | null => inclusiveUpper(current);

/**
 * The PATCH body for the picked values, or null when nothing changed.
 * Each picked last exclusive day is sent as the day after it. Empty and
 * unchanged fields are dropped so an untouched expired date is never
 * re-sent (the backend would reject it).
 */
export const buildExclusiveUntilPatch = (
  targets: FormatExclusiveTarget[],
  values: FormatExclusiveValues
): Partial<PublicationUpdateIn> | null => {
  const patch: Partial<PublicationUpdateIn> = {};

  targets.forEach((target) => {
    const value = values[target.key];
    if (!value) return;
    const next = value.add(1, 'day').format(WIRE_FORMAT);
    if (next === target.current) return;
    patch[target.key] = { id: target.id, exclusive_until: next };
  });

  return Object.keys(patch).length ? patch : null;
};
